import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, inr, fmtErr, fileUrl } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { ArrowLeft, Wallet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function GuestBillPage() {
  const [bill, setBill] = useState(null);
  const [paying, setPaying] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [payCfg, setPayCfg] = useState(null);
  const [qrBusy, setQrBusy] = useState(false);
  const nav = useNavigate();

  const load = useCallback(() => http.get("/guest/bill").then((r) => setBill(r.data)), []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { http.get("/public/payment-config").then((r) => setPayCfg(r.data)).catch(() => {}); }, []);

  const submitQr = async () => {
    if (!bill || bill.balance_due <= 0) return;
    setQrBusy(true);
    try {
      await http.post("/guest/payment-submitted", { amount: bill.balance_due });
      toast.success("Payment reported — the owner will verify it shortly. Your bill updates once verified.");
    } catch (e) { toast.error(fmtErr(e)); } finally { setQrBusy(false); }
  };

  const lineKeys = useMemo(() => {
    const seen = new Map();
    return (bill?.lines || []).map((l) => {
      const base = `${l.item_type}|${l.description}|${l.quantity}|${l.line_total}`;
      const n = (seen.get(base) || 0) + 1;
      seen.set(base, n);
      return `${base}|${n}`;
    });
  }, [bill]);

  const pay = async () => {
    if (!bill || bill.balance_due <= 0) return;
    setPaying(true);
    try {
      const r = await http.post("/guest/payment", { amount: bill.balance_due, method: "MOCK_UPI" });
      setBill({ ...bill, ...r.data.bill });
      toast.success(`Payment ${r.data.payment.gateway_transaction_id} verified`);
    } catch (e) { toast.error(fmtErr(e)); } finally { setPaying(false); }
  };

  const checkout = async () => {
    setCheckingOut(true);
    try {
      await http.post("/guest/checkout");
      nav("/guest/review");
    } catch (e) { toast.error(fmtErr(e)); } finally { setCheckingOut(false); }
  };

  if (!bill) return null;

  return (
    <div className="min-h-screen pb-20" style={{ background: "var(--surface)" }}>
      <div className="max-w-lg mx-auto px-4 pt-6">
        <BrandLogo className="h-9 mb-4" />
        <Link to="/guest/dashboard" data-testid="bill-back" className="flex items-center gap-1 text-sm mb-4" style={{ color: "var(--muted)" }}><ArrowLeft size={16} /> Back</Link>
        <div className="eyebrow mb-1">Running Bill</div>
        <h1 className="font-serif text-4xl mb-6">Your Stay Total</h1>

        <div className="card mb-4">
          {bill.lines.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No charges yet.</p>}
          {bill.lines.map((l, i) => (
            <div key={lineKeys[i]} className="flex justify-between items-start py-2 border-b last:border-0" style={{ borderColor: "var(--border)" }} data-testid={`bill-line-${i}`}>
              <div>
                <div className="font-medium text-sm">{l.description}</div>
                <div className="text-xs" style={{ color: "var(--muted)" }}>{l.item_type} · qty {l.quantity}</div>
              </div>
              <div className="font-medium">{inr(l.line_total)}</div>
            </div>
          ))}
        </div>

        <div className="card space-y-2">
          {bill.accommodation_subtotal > 0 && <Row label="Accommodation" value={inr(bill.accommodation_subtotal)} />}
          <Row label="Food subtotal" value={inr(bill.food_subtotal)} />
          <Row label="Services subtotal" value={inr(bill.service_subtotal)} />
          {bill.tax !== 0 && <Row label="Tax" value={inr(bill.tax)} />}
          <div className="hairline my-2" />
          <Row label="Grand total" value={inr(bill.grand_total)} big />
          <Row label="Paid" value={inr(bill.amount_paid)} good />
          <Row label="Balance due" value={inr(bill.balance_due)} accent />
        </div>

        {payCfg?.enabled && bill.balance_due > 0 && (
          <div className="card mt-6 text-center" data-testid="qr-payment-section">
            <div className="eyebrow mb-2">Pay by UPI</div>
            <div className="font-serif text-2xl mb-1">{payCfg.display_name || "UPI Payment"}</div>
            <div className="text-sm mb-4" style={{ color: "var(--terracotta)" }}>Amount due: {inr(bill.balance_due)}</div>
            <img src={fileUrl(payCfg.image_url)} alt="Payment QR" className="w-48 h-48 object-contain mx-auto rounded-xl border p-3 mb-3" style={{ borderColor: "var(--border)", background: "#fff" }} data-testid="qr-image" />
            {payCfg.instructions && <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>{payCfg.instructions}</p>}
            <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>Scanning the QR does not mark your bill paid automatically. After paying, tap below and the owner will verify.</p>
            <button data-testid="bill-qr-paid" onClick={submitQr} disabled={qrBusy} className="btn-pine w-full">
              {qrBusy ? "Sending…" : "I've Paid via QR — Notify Owner"}
            </button>
          </div>
        )}

        {bill.balance_due > 0 && (
          <button data-testid="bill-pay" onClick={pay} disabled={paying} className="btn-primary w-full mt-6 flex items-center justify-center gap-2">
            <Wallet size={18} /> {paying ? "Verifying…" : `Pay ${inr(bill.balance_due)} (Mock UPI)`}
          </button>
        )}

        {bill.balance_due <= 0.01 && (
          <button data-testid="bill-checkout" onClick={checkout} disabled={checkingOut} className="btn-pine w-full mt-6 flex items-center justify-center gap-2">
            <CheckCircle2 size={18} /> {checkingOut ? "Checking out…" : "Complete Checkout"}
          </button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, big, good, accent }) {
  return (
    <div className="flex justify-between items-center">
      <span className={big ? "font-serif text-xl" : "text-sm"} style={{ color: big ? "var(--fg)" : "var(--muted)" }}>{label}</span>
      <span className={big ? "font-serif text-2xl" : "font-medium"} style={{ color: accent ? "var(--terracotta)" : good ? "var(--pine)" : "var(--fg)" }}>{value}</span>
    </div>
  );
}
